import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { 
  TrendingUp, TrendingDown, Minus, Search, BarChart3, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Filter, Download
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

interface Keyword {
  id: string;
  keyword: string;
  page_url: string;
  search_volume: number | null;
  difficulty: number | null;
  current_ranking: number | null;
  target_ranking: number | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface KeywordTrackingDashboardProps {
  userId: string;
}

export const KeywordTrackingDashboard = ({ userId }: KeywordTrackingDashboardProps) => {
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'volume' | 'ranking' | 'difficulty'>('volume');

  useEffect(() => {
    fetchKeywords();
    
    const subscription = supabase
      .channel('keyword_tracking_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'seo_keywords' },
        () => fetchKeywords()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId]);

  const fetchKeywords = async () => {
    try {
      const { data, error } = await supabase
        .from('seo_keywords')
        .select('*')
        .order('search_volume', { ascending: false, nullsFirst: false });

      if (error) throw error;
      setKeywords(data || []);
    } catch (error) {
      console.error('Error fetching keywords:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = {
    total: keywords.length,
    tracked: keywords.filter(k => k.current_ranking !== null).length,
    top10: keywords.filter(k => k.current_ranking && k.current_ranking <= 10).length,
    top20: keywords.filter(k => k.current_ranking && k.current_ranking <= 20).length,
    avgVolume: Math.round(keywords.reduce((sum, k) => sum + (k.search_volume || 0), 0) / (keywords.length || 1)),
    avgDifficulty: Math.round(keywords.reduce((sum, k) => sum + (k.difficulty || 0), 0) / (keywords.length || 1)),
  };

  // Chart data - Volume distribution
  const volumeDistribution = [
    { name: 'Very High (10K+)', count: keywords.filter(k => (k.search_volume || 0) >= 10000).length, fill: 'hsl(var(--primary))' },
    { name: 'High (5K-10K)', count: keywords.filter(k => (k.search_volume || 0) >= 5000 && (k.search_volume || 0) < 10000).length, fill: 'hsl(var(--accent))' },
    { name: 'Medium (1K-5K)', count: keywords.filter(k => (k.search_volume || 0) >= 1000 && (k.search_volume || 0) < 5000).length, fill: 'hsl(var(--muted-foreground))' },
    { name: 'Low (<1K)', count: keywords.filter(k => (k.search_volume || 0) < 1000 && (k.search_volume || 0) > 0).length, fill: 'hsl(var(--border))' },
  ];

  // Ranking trend mock data (in real scenario, would come from seo_metrics_snapshots)
  const rankingTrend = [
    { date: 'Week 1', avgRank: 45 },
    { date: 'Week 2', avgRank: 38 },
    { date: 'Week 3', avgRank: 32 },
    { date: 'Week 4', avgRank: 28 },
    { date: 'Current', avgRank: 24 },
  ];

  // Filter and sort keywords
  const filteredKeywords = keywords
    .filter(k => {
      const matchesSearch = k.keyword.toLowerCase().includes(searchFilter.toLowerCase()) ||
                           k.page_url.toLowerCase().includes(searchFilter.toLowerCase());
      const matchesStatus = statusFilter === 'all' || k.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'volume':
          return (b.search_volume || 0) - (a.search_volume || 0);
        case 'ranking':
          if (!a.current_ranking) return 1;
          if (!b.current_ranking) return -1;
          return a.current_ranking - b.current_ranking;
        case 'difficulty':
          return (b.difficulty || 0) - (a.difficulty || 0);
        default:
          return 0;
      }
    });

  const getRankingBadge = (ranking: number | null) => {
    if (!ranking) return <Badge variant="outline">Not Ranked</Badge>;
    if (ranking <= 3) return <Badge className="bg-green-500">#{ranking}</Badge>;
    if (ranking <= 10) return <Badge className="bg-blue-500">#{ranking}</Badge>;
    if (ranking <= 20) return <Badge className="bg-yellow-500">#{ranking}</Badge>;
    return <Badge variant="secondary">#{ranking}</Badge>;
  };

  const getDifficultyColor = (difficulty: number | null) => {
    if (!difficulty) return 'text-muted-foreground';
    if (difficulty >= 80) return 'text-red-500';
    if (difficulty >= 60) return 'text-orange-500';
    if (difficulty >= 40) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getVolumeLabel = (volume: number | null) => {
    if (!volume) return '-';
    if (volume >= 10000) return `${(volume / 1000).toFixed(0)}K+`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(1)}K`;
    return volume.toString();
  };

  const exportCSV = () => {
    const headers = ['Keyword', 'Page URL', 'Search Volume', 'Difficulty', 'Current Ranking', 'Status'];
    const csvContent = [
      headers.join(','),
      ...filteredKeywords.map(k => [
        `"${k.keyword}"`,
        k.page_url,
        k.search_volume || '',
        k.difficulty || '',
        k.current_ranking || '',
        k.status || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keywords-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading keyword data...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Keyword Tracking Dashboard
          </h2>
          <p className="text-muted-foreground">
            Monitor ranking progress and search volume trends
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchKeywords}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Total Keywords</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-500">{stats.top10}</div>
            <p className="text-xs text-muted-foreground">Top 10 Rankings</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-500">{stats.top20}</div>
            <p className="text-xs text-muted-foreground">Top 20 Rankings</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.tracked}</div>
            <p className="text-xs text-muted-foreground">Tracked Keywords</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{getVolumeLabel(stats.avgVolume)}</div>
            <p className="text-xs text-muted-foreground">Avg. Volume</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className={`text-2xl font-bold ${getDifficultyColor(stats.avgDifficulty)}`}>
              {stats.avgDifficulty}%
            </div>
            <p className="text-xs text-muted-foreground">Avg. Difficulty</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Volume Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search Volume Distribution</CardTitle>
            <CardDescription>Keywords grouped by monthly search volume</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={volumeDistribution}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <YAxis tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))' 
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {volumeDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Ranking Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Average Ranking Trend</CardTitle>
            <CardDescription>Overall ranking improvement over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rankingTrend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <YAxis reversed tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))' 
                    }}
                    formatter={(value) => [`Position #${value}`, 'Avg. Rank']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="avgRank" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Keywords Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Keyword Performance</CardTitle>
          <CardDescription>All tracked keywords with ranking and volume data</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search keywords or pages..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="volume">Highest Volume</SelectItem>
                <SelectItem value="ranking">Best Ranking</SelectItem>
                <SelectItem value="difficulty">Highest Difficulty</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Keyword</TableHead>
                  <TableHead>Page URL</TableHead>
                  <TableHead className="text-center">Volume</TableHead>
                  <TableHead className="text-center">Difficulty</TableHead>
                  <TableHead className="text-center">Ranking</TableHead>
                  <TableHead className="text-center">Target</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredKeywords.slice(0, 50).map((keyword) => (
                  <TableRow key={keyword.id}>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {keyword.keyword}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                      {keyword.page_url}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-medium">{getVolumeLabel(keyword.search_volume)}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`font-medium ${getDifficultyColor(keyword.difficulty)}`}>
                        {keyword.difficulty ? `${keyword.difficulty}%` : '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {getRankingBadge(keyword.current_ranking)}
                    </TableCell>
                    <TableCell className="text-center">
                      {keyword.target_ranking ? `#${keyword.target_ranking}` : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={keyword.status === 'active' ? 'default' : 'secondary'}>
                        {keyword.status || 'pending'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {filteredKeywords.length > 50 && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Showing 50 of {filteredKeywords.length} keywords
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
